import React, { useState } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';

const MyFormsTour = ({ run, onComplete }) => {
  const [stepIndex, setStepIndex] = useState(0);

  const steps = [
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Welcome to Your Forms! 👋</h3>
          <p className="text-gray-700 mb-3">
            Congratulations on creating your first form! Let's take a quick tour to help you manage and share your forms effectively.
          </p>
          <p className="text-sm text-gray-500">
            You can skip this tour anytime by clicking "Skip" or pressing ESC.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="create-form-button"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Create New Forms</h3>
          <p className="text-gray-700 mb-2">
            Click here to create a new form anytime. You can create up to 5 forms on the free plan!
          </p>
          <p className="text-sm text-blue-600">
            💡Tip: Each form can have unlimited questions and responses.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="search-forms"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Search Your Forms</h3>
          <p className="text-gray-700">
            Use the search bar to quickly find forms by name or title. Perfect when you have many forms!
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="filter-forms"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Filter Forms by Status</h3>
          <p className="text-gray-700 mb-2">
            Filter forms by their status:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li><strong>All</strong> - View all your forms</li>
            <li><strong>Published</strong> - Forms that are live and collecting responses</li>
            <li><strong>Draft</strong> - Forms you're still working on</li>
          </ul>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="form-card"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Your Form Cards</h3>
          <p className="text-gray-700 mb-2">
            Each card shows important information about your form:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li>Form title and number of questions</li>
            <li>Response count and completion rate</li>
            <li>Status (Published or Draft)</li>
            <li>Last updated date</li>
          </ul>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="form-actions"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Form Actions Menu</h3>
          <p className="text-gray-700 mb-2">
            Click the three-dot menu to access actions:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li><strong>Edit</strong> - Modify your form</li>
            <li><strong>View Responses</strong> - See all submissions</li>
            <li><strong>Share</strong> - Get your form link</li>
            <li><strong>Duplicate</strong> - Create a copy</li>
            <li><strong>Delete</strong> - Remove the form</li>
          </ul>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: '[data-tour="view-responses"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">View Responses</h3>
          <p className="text-gray-700 mb-2">
            Click here to see all responses submitted to this form. You can:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li>View individual responses</li>
            <li>Export data to CSV</li>
            <li>Analyze response trends</li>
          </ul>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="share-form"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Share Your Form</h3>
          <p className="text-gray-700 mb-2">
            Click the share button to get your unique form link and share it with:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li>Email or messaging apps</li>
            <li>Social media platforms</li>
            <li>Embed on your website</li>
          </ul>
          <p className="text-sm text-blue-600 mt-2">
            💡Your form link never changes, even after editing!
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">You're All Set!🎉</h3>
          <p className="text-gray-700 mb-3">
            You now know how to manage, share, and track your forms! Start collecting responses and grow your data.
          </p>
          <p className="text-sm text-gray-600 mb-2">
            Need help? Look for the help button anytime to restart this tour.
          </p>
          <p className="text-sm text-blue-600">
            Happy form collecting! 
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status, action, index, type } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Tour completed or skipped
      if (onComplete) {
        onComplete();
      }
    } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      // Move to next step
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setStepIndex(nextStepIndex);
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      stepIndex={stepIndex}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#0066FF',
          textColor: '#374151',
          backgroundColor: '#FFFFFF',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          arrowColor: '#FFFFFF',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 12,
          padding: 20,
          fontSize: 14,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#0066FF',
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: 14,
          fontWeight: 600,
        },
        buttonBack: {
          color: '#6B7280',
          marginRight: 10,
        },
        buttonSkip: {
          color: '#9CA3AF',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      disableScrolling={false}
      spotlightPadding={4}
      disableOverlayClose
      hideCloseButton={false}
    />
  );
};

export default MyFormsTour;
